import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ShoppingBag, CircleDot } from "lucide-react";
import { PaymentMethodLabel } from "@printtat/ui";
import {
  ORDER_STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  type OrderListItem,
  type OrderStatus,
  type PaymentStatus,
} from "@printtat/shared";
import { getDefaultMockupPath } from "@/utils/productAssets";

interface OrderCardProps {
  order: OrderListItem;
}

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: "bg-yellow-50 text-yellow-700",
  confirmed: "bg-blue-50 text-blue-700",
  in_production: "bg-purple-50 text-purple-700",
  quality_check: "bg-indigo-50 text-indigo-700",
  shipped: "bg-cyan-50 text-cyan-700",
  out_for_delivery: "bg-teal-50 text-teal-700",
  delivered: "bg-green-50 text-green-700",
  cancelled: "bg-red-50 text-red-700",
  returned: "bg-orange-50 text-orange-700",
};

const PAYMENT_DOT_COLORS: Record<PaymentStatus, string> = {
  pending: "text-yellow-500",
  awaiting_payment: "text-amber-500",
  paid: "text-green-500",
  failed: "text-red-500",
  refund_pending: "text-orange-500",
  refunded: "text-gray-500",
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const OrderCard = ({ order }: OrderCardProps) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const statusColor =
    STATUS_COLORS[order.status as OrderStatus] || "bg-gray-50 text-gray-700";
  const paymentDotColor =
    PAYMENT_DOT_COLORS[order.paymentStatus as PaymentStatus] || "text-gray-400";

  const hasSrc = !imgError && (order.firstItemPreviewUrl || order.firstItemProductSlug);

  return (
    <button
      onClick={() => navigate(`/orders/${order.id}`)}
      className="w-full text-left bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all group"
    >
      <div className="flex items-center gap-4">
        {/* Preview thumbnail */}
        <div className="w-14 h-14 rounded-lg bg-gray-100 border border-gray-200 overflow-hidden flex-shrink-0">
          {hasSrc ? (
            <img
              src={order.firstItemPreviewUrl ?? getDefaultMockupPath(order.firstItemProductSlug!, "front")}
              alt={order.firstItemDesignName || "Order item"}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-gray-300" />
            </div>
          )}
        </div>

        {/* Order info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-semibold text-gray-900">
              {order.orderNumber}
            </p>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor}`}
            >
              {ORDER_STATUS_LABELS[order.status as OrderStatus] || order.status}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span>{formatDate(order.createdAt)}</span>
            <span>&middot;</span>
            <span>{order.itemCount} {order.itemCount === 1 ? "item" : "items"}</span>
            <span>&middot;</span>
            <PaymentMethodLabel
              paymentMethod={order.paymentMethod}
              paymentMethodDetail={order.paymentMethodDetail}
              cardBrand={order.cardBrand}
              cardLastFour={order.cardLastFour}
              iconSize="w-3 h-3"
            />
            <span>&middot;</span>
            <span className="inline-flex items-center gap-1">
              <CircleDot className={`w-3 h-3 ${paymentDotColor}`} />
              {PAYMENT_STATUS_LABELS[order.paymentStatus as PaymentStatus] || order.paymentStatus}
            </span>
          </div>
        </div>

        {/* Total + arrow */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <p className="text-sm font-semibold text-gray-900">
            {Number(order.totalAmount).toLocaleString()} EGP
          </p>
          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
        </div>
      </div>
    </button>
  );
};
